/**
 * Socket.IO Event Types for Frontend (TypeScript)
 * Mirrors backend event definitions for type safety
 */

// ============================================================================
// INVENTORY EVENTS
// ============================================================================

export interface InventoryUpdatePayload {
  packageId: string;
  departureDate: string;
  inventoryId?: string;
  rooms: {
    quadruple: number;
    triple: number;
    double: number;
    single: number;
  };
  capacity: {
    total: {
      quadruple: number;
      triple: number;
      double: number;
      single: number;
      total: number;
    };
    booked: {
      quadruple: number;
      triple: number;
      double: number;
      single: number;
      total: number;
    };
    available: {
      quadruple: number;
      triple: number;
      double: number;
      single: number;
      total: number;
    };
  };
  timestamp: string;
}

export interface AvailableDatesUpdatePayload {
  packageId: string;
  availableDates: Array<{
    date: string;
    rooms: {
      quadruple: number;
      triple: number;
      double: number;
      single: number;
    };
    isAvailable: boolean;
    isHoliday: boolean;
    holidayName?: string;
  }>;
  timestamp: string;
}

// ============================================================================
// BOOKING EVENTS
// ============================================================================

export interface BookingStatusUpdatePayload {
  bookingId: string;
  orderId?: string;
  status: "DRAFT" | "CONFIRMED" | "CANCELLED" | "PENDING";
  userId?: string;
  packageId: string;
  departureDate: string;
  timestamp: string;
}

export interface NewBookingPayload {
  bookingId: string;
  orderId: string;
  packageId: string;
  packageName: string;
  departureDate: string;
  totalAmount: number;
  currency: string;
  customerName: string;
  customerEmail: string;
  roomsCount: number;
  travelersCount: number;
  timestamp: string;
}

// ============================================================================
// ADMIN NOTIFICATION EVENTS
// ============================================================================

export interface AdminNotificationPayload {
  id: string;
  type:
    | "NEW_ORDER"
    | "PAYMENT_RECEIVED"
    | "PAYMENT_FAILED"
    | "PAYMENT_REMINDER"
    | "BOOKING_CONFIRMED"
    | "BOOKING_CANCELLED"
    | "DEPARTURE_FULLY_BOOKED"
    | "ROOM_TYPE_FULLY_BOOKED"
    | "ROOM_ASSIGNMENT_CONFLICT"
    | "DEPARTURE_APPROACHING"
    | "DOCUMENTS_UNVERIFIED"
    | "DOCUMENT_UPLOADED"
    | "DOCUMENT_VERIFIED"
    | "DOCUMENT_REJECTED"
    | "DOCUMENT_EXPIRING"
    | "HOTEL_ASSIGNMENT"
    | "HOTEL_CHANGED"
    | "DRAFT_BOOKING_REMINDER"
    | "LOW_INVENTORY"
    | "SYSTEM_ALERT"
    | "INQUIRY_RECEIVED"
    | "ORDER_NOTE";
  title: string;
  message: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  relatedId?: string;
  relatedOrderId?: string;
  relatedInventoryId?: string;
  relatedBookingId?: string;
  relatedTravelerId?: string;
  relatedDocumentId?: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

// ============================================================================
// PAYMENT EVENTS
// ============================================================================

export interface PaymentStatusUpdatePayload {
  orderId: string;
  bookingId: string;
  paymentStatus:
    | "PENDING"
    | "COMPLETED"
    | "FAILED"
    | "REFUNDED"
    | "PARTIAL_REFUND";
  amount: number;
  currency: string;
  userId?: string;
  timestamp: string;
}

// ============================================================================
// ORDER DOCUMENT EVENTS
// ============================================================================

export interface OrderDocumentUploadedPayload {
  orderId: string;
  userId?: string;
  document: {
    id: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    documentType: string;
    description?: string;
    uploadedAt: string;
    travelerId?: string;
    travelerName?: string;
  };
  timestamp: string;
}

// ============================================================================
// ORDER NOTE EVENTS
// ============================================================================

export interface OrderNoteAddedPayload {
  orderId: string;
  bookingId?: string;
  userId?: string; // User who owns the order (for user room emission)
  note: {
    content: string;
    author: string;
    createdAt: string;
  };
  timestamp: string;
}

export interface OrderNoteDeletedPayload {
  orderId: string;
  bookingId?: string;
  userId?: string; // User who owns the order (for user room emission)
  deletedNote: {
    content: string;
    author: string;
    createdAt: string;
  };
  remainingNotesCount: number;
  timestamp: string;
}

// ============================================================================
// DOCUMENT EVENTS
// ============================================================================

export interface DocumentUploadedPayload {
  document: {
    id: string;
    travelerId: string;
    bookingId?: string | null;
    documentType: string;
    documentNumber?: string | null;
    fileUrl: string;
    mimeType?: string;
    fileSize?: number;
    verificationStatus: "PENDING" | "VERIFIED" | "REJECTED";
    uploadedAt: string;
    expiryDate?: string | null;
  };
  traveler: {
    id: string;
    firstName: string;
    lastName: string;
    passportNumber?: string | null;
  };
  booking?: {
    id: string;
    bookingRef: string;
    bookingNumber?: number;
  } | null;
  isReupload: boolean;
  timestamp: string;
}

export interface DocumentStatusUpdatePayload {
  document: {
    id: string;
    travelerId: string;
    bookingId?: string | null;
    documentType: string;
    documentNumber?: string | null;
    fileUrl: string;
    mimeType?: string;
    fileSize?: number;
    verificationStatus: "PENDING" | "VERIFIED" | "REJECTED";
    verifiedAt?: string | null;
    verifiedBy?: string | null;
    rejectionReason?: string | null;
    uploadedAt: string;
    expiryDate?: string | null;
  };
  traveler: {
    id: string;
    firstName: string;
    lastName: string;
  };
  booking?: {
    id: string;
    bookingRef: string;
    bookingNumber?: number;
    userId?: string | null;
  } | null;
  userId?: string | null; // User who owns the booking (for user room emission)
  previousStatus: "PENDING" | "VERIFIED" | "REJECTED";
  newStatus: "PENDING" | "VERIFIED" | "REJECTED";
  timestamp: string;
}

// ============================================================================
// EVENT NAMES (Constants)
// ============================================================================

export const SOCKET_EVENTS = {
  // Connection
  CONNECTION: "connection",
  DISCONNECT: "disconnect",
  CONNECT_ERROR: "connect_error",
  CONNECTED: "connected",

  // Authentication
  AUTHENTICATE: "authenticate",
  AUTHENTICATION_SUCCESS: "authentication:success",
  AUTHENTICATION_ERROR: "authentication:error",

  // Inventory
  INVENTORY_UPDATE: "inventory:update",
  AVAILABLE_DATES_UPDATE: "inventory:dates:update",
  INVENTORY_LOW_STOCK: "inventory:low-stock",

  // Subscriptions
  SUBSCRIBE_TO_PACKAGE: "subscribe:package",
  SUBSCRIBE_TO_BOOKING: "subscribe:booking",
  SUBSCRIPTION_SUCCESS: "subscription:success",
  UNSUBSCRIBE: "unsubscribe",
  UNSUBSCRIPTION_SUCCESS: "unsubscription:success",

  // Booking
  BOOKING_STATUS_UPDATE: "booking:status:update",
  NEW_BOOKING: "booking:new",

  // Admin
  ADMIN_NOTIFICATION: "admin:notification",

  // User notifications
  NOTIFICATION_NEW: "notification:new",
  NOTIFICATION_UPDATE: "notification:update",

  // Payment
  PAYMENT_STATUS_UPDATE: "payment:status:update",

  // Order Documents
  ORDER_DOCUMENT_UPLOADED: "order:document:uploaded" as const,

  // Order Notes
  ORDER_NOTE_ADDED: "order:note:added" as const,
  ORDER_NOTE_DELETED: "order:note:deleted" as const,

  // Documents
  DOCUMENT_UPLOADED: "document:uploaded",
  DOCUMENT_STATUS_UPDATE: "document:status:update",
  DOCUMENT_VERIFIED: "document:verified",
  DOCUMENT_REJECTED: "document:rejected",
  DOCUMENT_UPLOAD_PROGRESS: "document:upload:progress",
  DOCUMENT_REVIEW: "document:review",
  SUBSCRIBE_TO_TRAVELER: "subscribe:traveler",

  // System
  SYSTEM_MAINTENANCE: "system:maintenance",
} as const;
